import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DevelopmentAreaInfo } from '@bo-client/shared/models/developmentAreas';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { Observable, Subject } from 'rxjs';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { filter, takeUntil } from 'rxjs/operators';
import { CommissioningDataService } from '@bo-client/activation-management/commissioning/forms/commissioning-data.service';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import { MetaService } from '@bo-client/core/api/meta-service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Component({
  selector: 'app-select-gis-data',
  templateUrl: './select-gis-data.component.html',
  styleUrls: ['./select-gis-data.component.scss']
})
export class SelectGisDataComponent implements OnInit, OnDestroy {
  @Input() areaType: AreaTypeEnum;
  @Input() areaTypes: AreaTypeEnum[];
  @Input() inputForm: FormGroup;
  @Input() inputFormAdditionalInfo: FormGroup;
  @Input() listDevelopmentAreas: DevelopmentAreaInfo[];
  @Input() disable = false;
  @Input() selectedGisData: GisDataModel;
  @Input() companyLocationsBytenant: CompanyLocationDto[];
  @Input() eventsFromParent: Observable<string>;
  @Input() askForDevelopmentArea = true;

  @Output() onSelectedGisData: EventEmitter<
    GisDataEvent & { currentCompanyLocationFromGis: CompanyLocationDto }
  > = new EventEmitter();
  @Output() resetCompanyLocation: EventEmitter<any> = new EventEmitter();

  private compUnsubs$ = new Subject<void>();
  public objectUsages$: Observable<any>;
  public gisDataForm: FormGroup;
  public gisDataHasChanged = false;
  public eventsToGis: Subject<string> = new Subject<string>();
  public isNewDevelopmentArea = false;
  public noCompanyLocation = false;
  public noAreaInThisCompanyLocation = false;

  constructor(
    public commissioningDataService: CommissioningDataService,
    private metaService: MetaService,
    private dataShareService: DataShareService
  ) {}

  ngOnInit(): void {
    this.objectUsages$ = this.metaService
      .codeListValues$('objectUsage_Types', this.dataShareService.currentTenant.id)
      .pipe(filter((x) => x.length > 0));

    this.eventsFromParent.pipe(takeUntil(this.compUnsubs$)).subscribe((x) => {
      this.eventsToGis.next(x);
    });
    if (this.inputForm.get('connectionObject').get('corridorNumber').value) {
      if (this.inputForm.get('connectionObject').get('corridorNumber').value !== '') {
        if (
          !this.inputForm.get('connectionObject').get('street') ||
          !this.inputForm.get('connectionObject').get('street').value
        ) {
          this.isNewDevelopmentArea = true;
        }
      }
    }
  }

  // tslint:disable-next-line:typedef
  setNullCompanyLocation($event) {
    if ($event) {
      this.noCompanyLocation = false;
      this.noAreaInThisCompanyLocation = false;
      this.resetCompanyLocation.emit();
    }
  }

  // Better: setData or setModel
  changeGisData(answer: {
    gisData: GisDataModel;
    objectNotFound: boolean;
    areaAvailable: boolean;
    currentCompanyLocationFromGis: CompanyLocationDto;
  }) {
    this.noCompanyLocation = false;
    this.noAreaInThisCompanyLocation = false;
    this.gisDataHasChanged = true;

    // Überprüfen, ob gisData im answer-Objekt verfügbar ist
    if (answer.gisData) {
      // Finden Sie das aktuelle Unternehmensstandort-Objekt in der Liste der Standorte
      const foundLocation = this.companyLocationsBytenant.find(
        (location) => location.external_id.toLowerCase() === answer.gisData.orgEinheit.toLowerCase()
      );

      // Wenn der Standort gefunden wurde, speichern Sie die Standortinformationen
      // im answer-Objekt und setzen Sie noCompanyLocation auf false
      // Wenn der Standort nicht gefunden wurde, setzen Sie currentCompanyLocationFromGis auf den ersten verfügbaren
      answer.currentCompanyLocationFromGis = foundLocation || this.companyLocationsBytenant[0] || null;
      this.noCompanyLocation = !answer.currentCompanyLocationFromGis;
    } else {
      // Wenn keine gisData verfügbar sind, setzen Sie currentCompanyLocationFromGis und noCompanyLocation auf true
      answer.currentCompanyLocationFromGis = null;
      this.noCompanyLocation = true;
    }

    // Wenn keine Fläche im aktuellen Standort verfügbar ist, setzen Sie currentCompanyLocationFromGis
    // auf null und noAreaInThisCompanyLocation auf true
    if (!answer.areaAvailable) {
      answer.currentCompanyLocationFromGis = null;
      this.noAreaInThisCompanyLocation = true;
    }

    // Speichern Sie die GIS-Daten und senden Sie das aktualisierte Answer-Objekt an andere Teile der Anwendung
    this.selectedGisData = answer.gisData;
    this.onSelectedGisData.emit(answer);
  }

  changeObjectType() {
    this.inputFormAdditionalInfo.get('numberOfFlats').patchValue(null);
    this.inputFormAdditionalInfo.get('numberOfUsers').patchValue(null);
    this.inputFormAdditionalInfo.get('numberOfOtherUsers').patchValue(null);
    this.inputFormAdditionalInfo.get('objectUsage').patchValue(null);
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
